import type { Breadcrumb, ErrorEntry } from './types';
import { sanitizePath } from './redact';

/**
 * Deux ANNEAUX indépendants, en mémoire, à coût passif nul (aucune sérialisation
 * hors du clic « Signaler un bug »).
 *
 * - Anneau principal (miettes nav/action/api) : au plus `MAIN_MAX` événements OU
 *   `MAIN_WINDOW_MS` de fenêtre glissante (au plus court ; on élague par le temps
 *   d'ABORD, puis par la taille).
 * - Anneau erreurs (séparé) : garde toujours les `ERROR_MAX` dernières erreurs —
 *   JAMAIS évincées par l'activité de miettes (la cause d'un crash survit aux
 *   clics suivants).
 *
 * Fusion : deux événements consécutifs de même signature n'en font qu'un, avec un
 * `count` incrémenté (le statut fait partie de la signature api → 200 puis 500 ne
 * fusionnent pas).
 */

export const MAIN_MAX = 30;
export const MAIN_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
export const ERROR_MAX = 8;

/** Instant du chargement de l'app : base des timings relatifs `t`. */
const loadEpoch = Date.now();

let breadcrumbs: Breadcrumb[] = [];
let errors: ErrorEntry[] = [];

function now(): { t: number; ts: string } {
  const abs = Date.now();
  return { t: abs - loadEpoch, ts: new Date(abs).toISOString() };
}

/** Signature de fusion : deux miettes consécutives de même signature se cumulent. */
function signatureOf(b: Breadcrumb): string {
  if (b.type === 'api') return `api|${b.method ?? ''}|${b.path ?? ''}|${b.status ?? ''}`;
  return `${b.type}|${b.code ?? ''}|${b.ids ? JSON.stringify(b.ids) : ''}`;
}

/** Élague l'anneau principal par la FENÊTRE de temps d'abord, puis par la TAILLE. */
function pruneMain(): void {
  const cutoff = Date.now() - loadEpoch - MAIN_WINDOW_MS;
  if (breadcrumbs.length > 0 && breadcrumbs[0]!.t < cutoff) {
    breadcrumbs = breadcrumbs.filter((b) => b.t >= cutoff);
  }
  if (breadcrumbs.length > MAIN_MAX) {
    breadcrumbs = breadcrumbs.slice(breadcrumbs.length - MAIN_MAX);
  }
}

/** Pousse une miette dans l'anneau principal (fusion si signature identique à la dernière). */
function pushBreadcrumb(b: Breadcrumb): void {
  const last = breadcrumbs[breadcrumbs.length - 1];
  if (last && signatureOf(last) === signatureOf(b)) {
    last.count = (last.count ?? 1) + 1;
    // On rafraîchit l'horodatage sur la dernière occurrence (activité récente).
    last.t = b.t;
    last.ts = b.ts;
    pruneMain();
    return;
  }
  breadcrumbs.push(b);
  pruneMain();
}

/** Enregistre une navigation (changement de route). */
export function recordNav(code: string, ids?: Record<string, number | string>): void {
  pushBreadcrumb({ ...now(), type: 'nav', code, ...(ids ? { ids } : {}) });
}

/** Enregistre une action métier (login, upload, partage…). */
export function recordAction(code: string, ids?: Record<string, number | string>): void {
  pushBreadcrumb({ ...now(), type: 'action', code, ...(ids ? { ids } : {}) });
}

/** Enregistre un appel API (métadonnées HTTP ; le `path` est assaini). */
export function recordApi(m: {
  method: string;
  path: string;
  status: number;
  ms: number;
  requestId: string | null;
}): void {
  pushBreadcrumb({
    ...now(),
    type: 'api',
    method: m.method,
    path: sanitizePath(m.path),
    status: m.status,
    ms: m.ms,
    requestId: m.requestId,
  });
}

/** Enregistre une erreur dans l'anneau dédié (jamais évincé par l'activité). */
export function recordError(message: string, source: ErrorEntry['source'], stack?: string): void {
  const entry: ErrorEntry = {
    ...now(),
    message,
    source,
    ...(stack ? { stack: stack.slice(0, 4000) } : {}),
  };
  const last = errors[errors.length - 1];
  if (last && last.message === entry.message && last.source === entry.source) {
    last.count = (last.count ?? 1) + 1;
    last.t = entry.t;
    last.ts = entry.ts;
    return;
  }
  errors.push(entry);
  if (errors.length > ERROR_MAX) errors = errors.slice(errors.length - ERROR_MAX);
}

/** Miettes courantes (élaguées), COPIE défensive. */
export function getBreadcrumbs(): Breadcrumb[] {
  pruneMain();
  return breadcrumbs.map((b) => ({ ...b }));
}

/** Erreurs courantes, COPIE défensive. */
export function getErrors(): ErrorEntry[] {
  return errors.map((e) => ({ ...e }));
}

/** Purge les deux anneaux (au logout : aucune fuite d'activité entre comptes). */
export function resetBuffers(): void {
  breadcrumbs = [];
  errors = [];
}
